import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import HeroSection from './components/HeroSection';
import CapabilitiesSection from './components/CapabilitiesSection';
import HowItWorksSection from './components/HowItWorksSection';
import CTASection from './components/CTASection';
import FooterSection from './components/FooterSection';
import FAQSection from './components/FAQSection';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import './landing.css';

const navLinks = [
  { href: '#features', label: 'Product' },
  { href: '#how-it-works', label: 'Process' },
  { href: '#faq', label: 'FAQ' },
];

const LandingPage = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
      setScrolled(window.scrollY > 24);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToWaitlist = () => {
    document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileOpen(false);
  };

  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));

  const toggleTheme = () => {
    const isDarkMode = document.documentElement.classList.contains('dark');
    if (isDarkMode) {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
      setIsDark(false);
    } else {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
      setIsDark(true);
    }
  };

  return (
    <div className="landing-page min-h-screen bg-[var(--lp-bg)] text-[var(--lp-ink)] transition-colors duration-300">
      <header
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
          scrolled ? 'border-b border-[var(--lp-line)] bg-[var(--lp-bg)]/90 backdrop-blur-md' : 'bg-transparent'
        }`}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-6 lg:px-8">
          <Link to="/" className="text-xl font-semibold tracking-tight text-[var(--lp-ink)]">
            prepx<span className="text-[var(--lp-accent)]">ai</span>
          </Link>

          <nav className="hidden items-center gap-8 md:flex">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="font-mono text-[11px] font-semibold tracking-wider text-[var(--lp-ink-soft)] transition-colors hover:text-[var(--lp-ink)]"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--lp-line)] text-[var(--lp-ink-soft)] transition-colors hover:text-[var(--lp-ink)]"
              aria-label="Toggle theme"
            >
              <Icon name={isDark ? 'Sun' : 'Moon'} size={16} />
            </button>

            <Button
              size="sm"
              onClick={scrollToWaitlist}
              className="hidden h-9 rounded-full bg-[var(--lp-accent)] px-5 text-xs font-semibold text-white hover:bg-[var(--lp-accent)]/90 sm:inline-flex"
            >
              Join waitlist
            </Button>

            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--lp-line)] md:hidden"
              onClick={() => setMobileOpen((o) => !o)}
              aria-label="Menu"
            >
              <Icon name={mobileOpen ? 'X' : 'Menu'} size={18} />
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="border-t border-[var(--lp-line)] bg-[var(--lp-bg)] px-5 py-4 md:hidden">
            <nav className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="font-mono text-xs font-semibold tracking-wider text-[var(--lp-ink)]"
                >
                  {link.label}
                </a>
              ))}
              <button
                type="button"
                onClick={scrollToWaitlist}
                className="mt-2 h-10 rounded-full bg-[var(--lp-accent)] text-sm font-semibold text-white hover:bg-[var(--lp-accent)]/90"
              >
                Join waitlist
              </button>
            </nav>
          </div>
        )}
      </header>

      <main>
        <HeroSection />
        <CapabilitiesSection />
        <HowItWorksSection />
        <FAQSection />
        <CTASection />
        <FooterSection />
      </main>

      {showScrollTop && (
        <Button
          variant="default"
          size="icon"
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-[90] h-10 w-10 rounded-full border border-[var(--lp-line)] bg-[var(--lp-surface)] text-[var(--lp-ink)] shadow-lg"
          aria-label="Scroll to top"
        >
          <Icon name="ArrowUp" size={18} />
        </Button>
      )}
    </div>
  );
};

export default LandingPage;
